import React from 'react';
import type { ReportSectionData, TestResult } from '../types';

interface TestSectionProps {
  section: ReportSectionData;
}

type RangeStatus = {
  outOfRange: boolean;
  flag: 'H' | 'L' | null;
};

const getRangeStatus = (resultValue: string | number, normalRange: string): RangeStatus => {
  const numericResult = parseFloat(String(resultValue));
  if (isNaN(numericResult)) return { outOfRange: false, flag: null };

  const lr = normalRange.toLowerCase();

  if (lr.startsWith('up to ') || lr.startsWith('random up to ')) {
    const limit = parseFloat(lr.replace(/up to|random/gi, '').trim());
    if (!isNaN(limit) && numericResult > limit) {
      return { outOfRange: true, flag: 'H' };
    }
  }

  if (lr.startsWith('<')) {
    const limit = parseFloat(lr.replace('<', '').trim());
    if (!isNaN(limit) && numericResult >= limit) {
      return { outOfRange: true, flag: 'H' };
    }
  }

  if (lr.includes('-')) {
    const rangeParts = lr.split('sufficient:')[1] || lr;
    const parts = rangeParts.split('-').map(s => s.trim());
    if (parts.length === 2) {
      const [min, max] = parts.map(parseFloat);
      if (!isNaN(min) && !isNaN(max)) {
        if (numericResult < min) return { outOfRange: true, flag: 'L' };
        // FIX: Corrected typo from `out ofRange` to `outOfRange`
        if (numericResult > max) return { outOfRange: true, flag: 'H' };
      }
    }
  }

  return { outOfRange: false, flag: null };
};

const TestSection: React.FC<TestSectionProps> = ({ section }) => {
  return (
    <div className="mb-8 avoid-break-inside">
      <h3 className="text-lg font-bold text-slate-800 tracking-wide border-b-2 border-slate-300 pb-2 mb-3">
        {section.title}
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <thead>
            <tr>
              <th className="py-2 px-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-[40%]">Test</th>
              <th className="py-2 px-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-[15%]">Result</th>
              <th className="py-2 px-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-[10%]">Unit</th>
              <th className="py-2 px-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-[35%]">Reference Range</th>
            </tr>
          </thead>
          <tbody>
            {section.results.map((result, index) => {
              const status = getRangeStatus(result.value, result.normalRange);
              const isAbnormal = status.outOfRange;

              return (
                <tr key={index} className="border-b border-slate-200/80">
                  <td className={`py-3 px-2 text-sm ${isAbnormal ? 'font-bold text-slate-900' : 'text-slate-700'}`}>
                    {result.name}
                  </td>
                  <td className="py-3 px-2 text-sm text-center">
                    <span className={`${isAbnormal ? 'font-bold text-brand-dark' : 'text-slate-800'}`}>
                      {result.value}
                    </span>
                    {isAbnormal && (
                      <span className="ml-1.5 text-brand-dark font-bold text-xs" aria-label={status.flag === 'H' ? 'High' : 'Low'}>
                        {status.flag}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-sm text-slate-500">
                    {result.unit}
                  </td>
                  <td className="py-3 px-2 text-sm text-slate-500">
                    {result.normalRange}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TestSection;